import {
  arrow,
  autoUpdate,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import type { CSSProperties } from 'react'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import type { Decoration } from 'src/services/apollo/generated/graphql'
import { type UserBaseFragment } from 'src/services/apollo/generated/graphql'
import { cn } from 'src/utils/cx'
import { UITooltipResponsive } from '../HelpIcon'
import { MembershipIconCircled } from '../Membership/MembershipIcon'
import { resolveAvatarBadgeData } from './user-avatar-badge'

export type AvatarBadgeProps = StyleProps & {
  user: Pick<UserBaseFragment, 'membership' | 'activeDecorations'>
  /**
   * True: display the user's active badge
   * False: display nothing
   * If decoration is provided, will override user's active badge
   */
  decorationBadge: Decoration | boolean
  remarkDisplayType?: false | 'auto'
}

export default function AvatarBadge({
  user,
  decorationBadge,
  remarkDisplayType = false,
  className,
  style,
}: AvatarBadgeProps) {
  const resolvedBadge = useMemo(
    () => resolveAvatarBadgeData(user, decorationBadge),
    [user, decorationBadge],
  )

  if (!resolvedBadge) return null

  if (resolvedBadge.type === 'membership') {
    return (
      <div className={className} style={style}>
        <MembershipIconCircled
          className={cn('rounded-full border border-white size-full')}
        />
      </div>
    )
  }

  const badgeUrl = resolvedBadge.decorationData.assetUrl
  const shouldShowAutoTooltip =
    remarkDisplayType === 'auto' && badgeUrl && resolvedBadge.userRemark

  if (shouldShowAutoTooltip) {
    return (
      <AutoOpenTooltipWrapper
        badgeUrl={badgeUrl}
        userRemark={resolvedBadge.userRemark!}
        className={className}
        style={style}
      />
    )
  }

  return (
    <Suspense>
      {badgeUrl ? (
        <div className={className} style={style}>
          <img src={badgeUrl} alt="" />
        </div>
      ) : null}
    </Suspense>
  )
}

function AutoOpenTooltipWrapper({
  badgeUrl,
  userRemark,
  className,
  style,
}: {
  badgeUrl: string
  userRemark: string
  className?: string
  style?: CSSProperties
}) {
  const [isAutoOpenPhase, setIsAutoOpenPhase] = useState(true)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const arrowRef = useRef<HTMLDivElement>(null)

  const { refs, floatingStyles, middlewareData } = useFloating({
    placement: 'right',
    middleware: [
      offset(10),
      // flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  })

  // TODO: Optimize the animation
  // Check if element is in viewport before triggering auto-open
  useEffect(() => {
    const element = buttonRef.current
    if (!element) return

    // Check in viewport
    const observer = new IntersectionObserver(
      ([entry], observer) => {
        if (entry.isIntersecting) {
          // Element is visible, start auto-open phase
          setIsPopoverOpen(true)

          const closeTimer = setTimeout(() => {
            setIsPopoverOpen(false)
            // Transition to tooltip mode after closing
            setTimeout(() => {
              setIsAutoOpenPhase(false)
            }, 200) // Wait for close animation
          }, 3000)

          // Cleanup
          observer.unobserve(element)
          observer.disconnect()
          return () => clearTimeout(closeTimer)
        } else {
          // Element is not visible, skip auto-open phase
          setIsAutoOpenPhase(false)
          // Cleanup early
          observer.unobserve(element)
          observer.disconnect()
        }
      },
      {
        rootMargin: '-96px 0px 0px 0px',
        threshold: 1.0,
      },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
      observer.disconnect()
    }
  }, [])

  // floating-ui: Sync refs or arrow position will be incorrect
  useEffect(() => {
    if (buttonRef.current) {
      refs.setReference(buttonRef.current)
    }
  }, [])

  const placement = 'right bottom'

  const tooltipContent = (
    <div className="p-3">
      <p className="text-center font-medium">{userRemark}</p>
    </div>
  )

  // During auto-open phase, use custom floating tooltip
  if (isAutoOpenPhase) {
    return (
      <>
        <button
          ref={buttonRef}
          aria-label={userRemark}
          className={cn(className, 'outline-none')}
          style={style}
        >
          <img src={badgeUrl} alt="" />
        </button>
        <AnimatePresence>
          {isPopoverOpen && (
            <motion.div
              ref={refs.setFloating}
              // Not touching transform related values so it won't conflict with floating's styles
              // Transitioning fade-in/out
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn(
                /** Higher than EditIcon */
                'z-[2]',
              )}
              style={{
                ...floatingStyles,
              }}
            >
              <motion.div
                // Animating bounce
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 1,
                  repeat: 3,
                  type: 'tween',
                }}
                className={cn(
                  'pointer-events-none',
                  'rounded-lg drop-shadow-lg bg-modal-background',
                  'max-w-[30em]',
                )}
              >
                <div
                  ref={arrowRef}
                  style={{
                    position: 'absolute',
                    left: middlewareData.arrow?.x,
                    top: middlewareData.arrow?.y,
                  }}
                  className="size-2 rotate-45 bg-modal-background -translate-x-1/2 transition-colors"
                />
                <div className="overflow-auto text-sm whitespace-nowrap">
                  {tooltipContent}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    )
  }

  // After auto-open phase, use UITooltipResponsive
  return (
    <UITooltipResponsive
      trigger={open => (
        <AriaButton
          onPress={open}
          aria-label={userRemark}
          className={cn(className, 'outline-none')}
          style={style}
        >
          <img src={badgeUrl} alt="" />
        </AriaButton>
      )}
      TooltipProps={{ placement }}
    >
      {tooltipContent}
    </UITooltipResponsive>
  )
}
